/**
 * "Werte mitschicken" in the plugin settings: which calculated form variables
 * a lead plugin passes on (spec 2026-09-17-werte-an-lead-formulare-design.md).
 * Stored as settings.fieldMap = [{ field, source }], source = form.<id>.<varId>.
 * The reader resolves it in files/_app_dev/js/fieldMap.js.
 *
 * Plain JavaScript, no Vue: Jest cannot resolve .ts in this part of the app.
 */
import { derivedFormFields } from '@/components/modals/formDerivedFields';

export const SOURCE_PATH = /^form\.([0-9]+)\.(var_[A-Za-z0-9_-]+)$/;

function parsedDoc(formData) {
  if (typeof formData !== 'string') return formData && typeof formData === 'object' ? formData : null;
  try { return JSON.parse(formData); } catch (e) { return null; }
}

// getFormsByPath answers with rows for sys admins and with one aggregated
// row { forms: [...] } for everybody else; both end up here as one list.
export function normalizeForms(raw) {
  if (!Array.isArray(raw)) return [];
  const flat = [];
  raw.forEach((item) => {
    if (item && Array.isArray(item.forms)) flat.push(...item.forms);
    else flat.push(item);
  });
  const seen = new Set();
  const out = [];
  flat.forEach((form) => {
    if (!form || form.form_id === null || form.form_id === undefined) return;
    if (seen.has(form.form_id)) return;
    seen.add(form.form_id);
    out.push({ ...form, form_data: parsedDoc(form.form_data) });
  });
  return out;
}

export function variableGroups(rawForms) {
  const groups = [];
  normalizeForms(rawForms).forEach((form) => {
    const options = derivedFormFields(form.form_data || {})
      .filter((field) => field.derived === 'variable')
      .map((field) => ({
        value: `form.${form.form_id}.${field.fieldId}`,
        text: `${form.name} · ${field.label}`,
      }));
    if (options.length) groups.push({ label: form.name, options });
  });
  return groups;
}

export function isOrphanSource(source, groups) {
  if (!source) return false;
  return !groups.some((group) => group.options.some((option) => option.value === source));
}

// What is saved: complete rows only. An orphaned path is kept on purpose, the
// row shows "Wert nicht mehr vorhanden" until the customer removes it.
export function cleanFieldMap(rows) {
  if (!Array.isArray(rows)) return [];
  return rows
    .filter((row) => row && typeof row.field === 'string' && typeof row.source === 'string')
    .map((row) => ({ field: row.field.trim(), source: row.source }))
    .filter((row) => row.field !== '' && SOURCE_PATH.test(row.source));
}

// The form builder (editor-v2, own iframe) gets the forms whose variables a
// hidden field may carry. Plain data only: it crosses postMessage.
export function builderValueForms(rawForms) {
  return normalizeForms(rawForms)
    .map((form) => ({
      formId: Number(form.form_id),
      name: form.name || `#${form.form_id}`,
      variables: (form.form_data && Array.isArray(form.form_data.variables) ? form.form_data.variables : [])
        .filter((v) => v && typeof v.id === 'string' && /^var_[A-Za-z0-9_-]+$/.test(v.id))
        .map((v) => ({ id: v.id, name: v.name || v.id })),
    }))
    .filter((form) => form.variables.length > 0);
}
